import React, { useEffect, useState, useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import RenderField, { FieldInTicket } from '../../RenderField'
import { useForm, FormProvider } from 'react-hook-form'
import * as S from './style'
import { MoreHorizontal, Calendar, Paperclip, Plus, Copy, Link2, Archive, Trash2, Pencil, ExternalLink, Download, FileText, MoreVertical } from 'lucide-react'
import CommentsOfOp from '../../../Modals/ViewPCP/Comments'
import { coverUrlManager } from '../../../../utils/coverUrlManager'

interface TicketTask {
  id: number
  title: string
  completed: boolean
  ticketId: number
  createdAt: string
}

interface Attachment {
  id: number
  originalName: string
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
}

interface TagInTicket {
  tagId: number
  ticketId: number
  tag: {
    id: number
    name: string
    color: string
  }
}

interface Step {
  id: number
  name: string
  pipelineId: number
  position?: number
}

export interface TicketFile {
  generalFile: {
    id: number;
    fileUrl: string;
    mimeType: string;
    title: string;
  };
}

interface Ticket {
  id: number
  title: string
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  createdAt: string
  fieldInTickets?: FieldInTicket[]
  tags?: TagInTicket[]
  tasks?: TicketTask[]
  attachments: Attachment[]
  fileInTickets?: TicketFile[];
  coverFile?: {
    id: number;
    fileUrl: string;
    title: string;
    originalName: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  };
  step?: {
    id: number
    name: string
    pipelineId: number
  }
}

interface Tag {
  id: number
  name: string
  color: string
}

const actionButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '12px',
  color: '#475569',
  textAlign: 'left',
  transition: 'background-color 0.2s'
};

const ViewOrEditTicket: React.FC = () => {
  const [searchParams] = useSearchParams()
  const ticketId = searchParams.get('ticketId')
  const isFilled = searchParams.get('isFilled')
  const isFilledBoolean = isFilled === 'false' ? false : true
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [optionsMenuPosition, setOptionsMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const optionsButtonRef = useRef<HTMLButtonElement | null>(null)
  const optionsMenuRef = useRef<HTMLDivElement | null>(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [showAddTask, setShowAddTask] = useState(false)
  const [attachmentMenuId, setAttachmentMenuId] = useState<number | null>(null)
  const [attachmentMenuPosition, setAttachmentMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const attachmentMenuRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const attachmentDropdownRef = useRef<HTMLDivElement | null>(null)
  const [taskMenuId, setTaskMenuId] = useState<number | null>(null)
  const [taskMenuPosition, setTaskMenuPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
  const taskMenuRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const taskDropdownRef = useRef<HTMLDivElement | null>(null)
  const tagDropdownRef = useRef<HTMLDivElement | null>(null)
  const tagContainerRef = useRef<HTMLDivElement | null>(null)
  const [coverImage, setCoverImage] = useState<string | undefined>();

  const methods = useForm()
  const { control } = methods

  const navigate = useNavigate()


  const normalizeFullTicket = (data: any) => {
    if (!data) return data
    const mapped: any = { ...data }
    if (data.GeneralFile) {
      mapped.coverFile = {
        id: data.GeneralFile.id,
        fileUrl: data.GeneralFile.fileUrl,
        title: data.GeneralFile.title,
        originalName: data.GeneralFile.originalName,
        fileName: data.GeneralFile.fileName,
        fileSize: data.GeneralFile.fileSize,
        mimeType: data.GeneralFile.mimeType
      }
    }
    return mapped
  }
  useEffect(() => {
    const updateCoverUrl = async () => {
      if (ticket?.coverFile?.fileUrl && ticket.coverFile.id) {
        try {
          const url = await coverUrlManager.getCoverUrl(ticket.coverFile.id, ticket.coverFile.fileUrl);
          setCoverImage(url);
        } catch (error) {
          console.error('Erro ao obter URL da capa:', error);
          setCoverImage(ticket.coverFile.fileUrl);
        }
      } else {
        setCoverImage(undefined);
      }
    };

    updateCoverUrl();
  }, [ticket?.coverFile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (attachmentDropdownRef.current && !attachmentDropdownRef.current.contains(e.target as Node)) {
        const isMenuButton = Object.values(attachmentMenuRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isMenuButton) {
          setAttachmentMenuId(null)
        }
      }
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(e.target as Node)) {
        const isTaskMenuButton = Object.values(taskMenuRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isTaskMenuButton) {
          setTaskMenuId(null)
        }
      }
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        const isTagContainer = tagContainerRef.current && tagContainerRef.current.contains(e.target as Node)
        if (!isTagContainer) {
          setShowTagDropdown(false)
        }
      }
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target as Node)) {
        const isOptionsButton = optionsButtonRef.current && optionsButtonRef.current.contains(e.target as Node)
        if (!isOptionsButton) {
          setShowOptionsMenu(false)
        }
      }
    }
    const handleScrollOrResize = () => {
      setAttachmentMenuId(null)
      setTaskMenuId(null)
      setShowOptionsMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const handleToggleAttachmentMenu = (attachmentId: number) => {
    if (attachmentMenuId === attachmentId) {
      setAttachmentMenuId(null)
      return
    }
    const button = attachmentMenuRefs.current[attachmentId]
    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 120
      const menuHeight = 120
      let left = rect.left
      let top = rect.bottom + 5
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5
      }
      setAttachmentMenuPosition({ top, left })
    }
    setAttachmentMenuId(attachmentId)
  }

  const handleToggleTaskMenu = (taskId: number) => {
    if (taskMenuId === taskId) {
      setTaskMenuId(null)
      return
    }
    const button = taskMenuRefs.current[taskId]
    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 100
      const menuHeight = 50
      let left = rect.right - menuWidth
      let top = rect.bottom + 5
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5
      }
      setTaskMenuPosition({ top, left })
    }
    setTaskMenuId(taskId)
  }

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
        if (response.data) {
          const normalized = normalizeFullTicket(response.data.data)
          setTicket(normalized)

          const stepResponse = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, {
            params: { pipelineId: response.data.data.step?.pipelineId }
          })
          if (stepResponse.data) {
            setSteps(stepResponse.data.data || [])
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    const fetchTags = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`)
        if (response.data) {
          setAllTags(response.data.data || [])
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchTicket()
    fetchTags()
  }, [ticketId])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return '🖼️'
    if (mimeType.startsWith('video/')) return '🎬'
    if (mimeType === 'application/pdf') return '📄'
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊'
    if (mimeType.includes('document') || mimeType.includes('word')) return '📝'
    return '📁'
  }

  const handleSave = async () => {
    if (!ticket) return

    const errors: string[] = []
    const isEmail = (v: string) => /.+@.+\..+/.test(v)
    const onlyDigits = (v: string) => v.replace(/\D/g, '')
    const isCpfValid = (v: string) => onlyDigits(v).length === 11
    const isCnpjValid = (v: string) => onlyDigits(v).length === 14
    const isCelularValid = (v: string) => {
      const d = onlyDigits(v)
      return d.length >= 10 && d.length <= 11
    }
    const isTelefoneValid = (v: string) => onlyDigits(v).length === 10

    if (ticket.fieldInTickets && Array.isArray(ticket.fieldInTickets)) ticket.fieldInTickets.forEach((f) => {
      const typeName = f.pipelineField.typeField?.name || ''
      const hasIntegration = !!f.pipelineField.integrationConfig

      const isStringType = ['E-mail', 'CPF', 'CNPJ', 'Celular', 'Telefone', 'Texto simples'].includes(typeName)
      const isDateType = typeName === 'Data'
      const isCheckboxType = typeName === 'Checkbox'
      const isValorType = typeName === 'Valor (R$)'

      if (f.isRequired || f.pipelineField.integrationConfig?.isRequired) {
        if (hasIntegration && !f.integerValue) {
          errors.push(`Selecione um valor para ${f.pipelineField.name}`)
        } else if (!hasIntegration) {
          if (isStringType && (!f.stringValue || f.stringValue.trim() === '')) errors.push(`Preencha o campo ${f.pipelineField.name}`)
          if (isDateType && !f.dateTimeValue) errors.push(`Preencha o campo ${f.pipelineField.name}`)
          if (isCheckboxType && f.booleanValue === undefined) errors.push(`Marque o campo ${f.pipelineField.name}`)
          if (isValorType && (f.decimalValue === undefined || f.decimalValue === null)) errors.push(`Preencha o campo ${f.pipelineField.name}`)
        }
      }

      if (typeName === 'E-mail' && f.stringValue && !isEmail(f.stringValue)) errors.push(`E-mail inválido em ${f.pipelineField.name}`)
      if (typeName === 'CPF' && f.stringValue && !isCpfValid(f.stringValue)) errors.push(`CPF inválido em ${f.pipelineField.name}`)
      if (typeName === 'CNPJ' && f.stringValue && !isCnpjValid(f.stringValue)) errors.push(`CNPJ inválido em ${f.pipelineField.name}`)
      if (typeName === 'Celular' && f.stringValue && !isCelularValid(f.stringValue)) errors.push(`Celular inválido em ${f.pipelineField.name}`)
      if (typeName === 'Telefone' && f.stringValue && !isTelefoneValid(f.stringValue)) errors.push(`Telefone inválido em ${f.pipelineField.name}`)
    })

    if (errors.length > 0) {
      alert(errors[0])
      return
    }

    try {
      const formData = new FormData();

      const jsonData = {
        title: ticket.title,
        description: ticket.description,
        deadline: ticket.deadline,
        urgency: ticket.urgency,
        fieldInTickets: ticket.fieldInTickets
      };

      Object.entries(jsonData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (key === 'fieldInTickets') {
          formData.append(key, JSON.stringify(value));
        } else if (value instanceof Date) {
          formData.append(key, value.toISOString());
        } else {
          formData.append(key, String(value));
        }
      });

      const response = await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data) {
        navigate(-1)
      }
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || 'Erro ao salvar. Tente novamente.'
      alert(errorMessage)
    }
  }

  const handleStepChange = async (newStepId: number) => {
    if (!ticket || newStepId === ticket.stepId) return

    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`,
        {
          title: ticket.title,
          description: ticket.description,
          deadline: ticket.deadline,
          urgency: ticket.urgency,
          fieldInTickets: ticket.fieldInTickets,
          stepId: newStepId
        }
      )

      const newStep = steps.find(s => s.id === newStepId)
      setTicket(prev => prev ? {
        ...prev,
        stepId: newStepId,
        step: newStep ? { id: newStep.id, name: newStep.name, pipelineId: newStep.pipelineId } : prev.step
      } : null)
    } catch (error: any) {
      console.error(error)
      const errorMessage = error?.response?.data?.message || 'Erro ao mudar de etapa. Tente novamente.'
      alert(errorMessage)
    }
  }

  const handleAddTag = async (tagId: number) => {
    if (!ticket) return

    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tags/${tagId}`)
      if (response.data?.data) {
        setTicket(prev => prev ? {
          ...prev,
          tags: [...(prev.tags ?? []), response.data.data]
        } : null)
      }
      setShowTagDropdown(false)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveTag = async (tagId: number) => {
    if (!ticket) return

    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tags/${tagId}`)
      setTicket(prev => prev ? {
        ...prev,
        tags: prev.tags?.filter(t => t.tagId !== tagId) ?? []
      } : null)
    } catch (error) {
      console.error(error)
    }
  }

  const getAvailableTags = () => {
    if (!ticket) return allTags
    const currentTagIds = (ticket.tags || []).map(t => t.tagId)
    return allTags.filter(tag => !currentTagIds.includes(tag.id))
  }

  const handleToggleTask = async (taskId: number, completed: boolean) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tasks/${taskId}`, {
        completed: !completed
      })
      setTicket(prev => prev ? {
        ...prev,
        tasks: prev.tasks?.map(t => t.id === taskId ? { ...t, completed: !completed } : t) ?? []
      } : null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return

    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tasks`, {
        title: newTaskTitle
      })
      if (response.data) {
        setTicket(prev => prev ? {
          ...prev,
          tasks: [...(prev.tasks ?? []), response.data.data]
        } : null)
        setNewTaskTitle('')
        setShowAddTask(false)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteTask = async (taskId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/tasks/${taskId}`)
      setTicket(prev => prev ? {
        ...prev,
        tasks: prev.tasks?.filter(t => t.id !== taskId) ?? []
      } : null)
      setTaskMenuId(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const attachments: { fileName: string; file: string }[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.readAsDataURL(file)
      })
      attachments.push({ fileName: file.name, file: base64 })
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments`, {
        attachments
      })
      if (response.data) {
        setTicket(prev => prev ? {
          ...prev,
          attachments: [...prev.attachments, ...response.data.data]
        } : null)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleViewAttachment = async (attachment: Attachment) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachment.id}/view`)
      window.open(response.data.url, '_blank')
    } catch (error) {
      console.error(error)
    }
    setAttachmentMenuId(null)
  }

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachment.id}/download`)
      const link = document.createElement('a')
      link.href = response.data.url
      link.download = attachment.originalName
      link.click()
    } catch (error) {
      console.error(error)
    }
    setAttachmentMenuId(null)
  }

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/attachments/${attachmentId}`)
      setTicket(prev => prev ? {
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== attachmentId)
      } : null)
      setAttachmentMenuId(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSetCover = async (fileId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/cover/${fileId}`)

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
      if (response.data) {
        setTicket(normalizeFullTicket(response.data.data))
      }
      setAttachmentMenuId(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveCover = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/cover`)

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
      if (response.data) {
        setTicket(normalizeFullTicket(response.data.data))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleOptionsMenu = () => {
    if (showOptionsMenu) {
      setShowOptionsMenu(false)
      return
    }
    if (optionsButtonRef.current) {
      const rect = optionsButtonRef.current.getBoundingClientRect()
      const menuWidth = 160
      const menuHeight = 180
      let left = rect.right - menuWidth
      let top = rect.bottom + 8
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 8
      }
      setOptionsMenuPosition({ top, left })
    }
    setShowOptionsMenu(true)
  }

  const handleDuplicateTicket = async () => {
    if (!ticket) return
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: `${ticket.title} (cópia)`,
        stepId: ticket.stepId
      })
      if (response.data) {
        setShowOptionsMenu(false)
        navigate(`/panel/pipeline/ticket?ticketId=${response.data.data.id}`)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyLink = () => {
    const url = `${window.location.origin}/panel/pipeline/ticket?ticketId=${ticketId}`
    navigator.clipboard.writeText(url)
    alert('Link copiado para a área de transferência')
    setShowOptionsMenu(false)
  }

  const handleStartRename = () => {
    if (!ticket) return
    setTitleDraft(ticket.title)
    setIsEditingTitle(true)
    setShowOptionsMenu(false)
  }

  const handleConfirmRename = async () => {
    if (!ticket || !titleDraft.trim()) return
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: titleDraft.trim()
      })
      setTicket({ ...ticket, title: titleDraft.trim() })
      setIsEditingTitle(false)
    } catch (error) {
      console.error('Erro ao renomear ticket:', error)
    }
  }

  const handleCancelRename = () => {
    setIsEditingTitle(false)
    setTitleDraft('')
  }

  const handleArchiveTicket = async () => {
    if (!ticket) return
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/archive`)
      setShowOptionsMenu(false)
      navigate(-1)
    } catch (error) {
      console.error('Erro ao arquivar ticket:', error)
    }
  }

  const handleDeleteTicket = async () => {
    if (!ticket) return
    if (!window.confirm('Tem certeza que deseja excluir este card?')) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`)
      setShowOptionsMenu(false)
      navigate(-1)
    } catch (error) {
      console.error('Erro ao excluir ticket:', error)
    }
  }

  if (!ticket) return null

  return (
    <FormProvider {...methods}>
      <S.Overlay onClick={() => navigate(-1)} />
      <S.Container>
        <S.Header>
          <S.HeaderLeft>
            {isEditingTitle ? (
              <S.TitleEditWrapper>
                <S.TitleInput
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirmRename()
                    if (e.key === 'Escape') handleCancelRename()
                  }}
                  autoFocus
                />
                <S.TitleEditButtons>
                  <button onClick={handleConfirmRename}>Salvar</button>
                  <button onClick={handleCancelRename} className="cancel">Cancelar</button>
                </S.TitleEditButtons>
              </S.TitleEditWrapper>
            ) : (
              <>
                <S.Title>{ticket.title}</S.Title>
                <S.CreatedAt>Criado em {formatDate(ticket.createdAt)}</S.CreatedAt>
              </>
            )}
          </S.HeaderLeft>
          <S.HeaderRight>
            <S.OptionsButton ref={optionsButtonRef} onClick={handleToggleOptionsMenu}>
              <MoreHorizontal size={18} />
            </S.OptionsButton>
            {showOptionsMenu && ReactDOM.createPortal(
              <S.AttachmentDropdownPortal
                ref={optionsMenuRef}
                style={{ top: optionsMenuPosition.top, left: optionsMenuPosition.left }}
                onClick={(e) => e.stopPropagation()}
              >
                <S.DropdownItem onClick={handleDuplicateTicket}>
                  <Copy size={16} /> Duplicar
                </S.DropdownItem>
                <S.DropdownItem onClick={handleStartRename}>
                  <Pencil size={16} /> Renomear
                </S.DropdownItem>
                <S.DropdownItem onClick={handleCopyLink}>
                  <Link2 size={16} /> Copiar link
                </S.DropdownItem>
                <S.DropdownItem onClick={handleArchiveTicket}>
                  <Archive size={16} /> Arquivar
                </S.DropdownItem>
                <S.DropdownItem className="danger" onClick={handleDeleteTicket}>
                  <Trash2 size={16} /> Excluir
                </S.DropdownItem>
              </S.AttachmentDropdownPortal>,
              document.body
            )}
            <S.SaveButton onClick={handleSave}>
              <Calendar size={16} />
              Salvar e fechar
            </S.SaveButton>
          </S.HeaderRight>
        </S.Header>

        <S.Content>
          {coverImage && (
            <div style={{ width: '100%', marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', position: 'relative', maxHeight: '400px' }}>
              <img
                src={coverImage}
                alt="Capa do Ticket"
                style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain', display: 'block' }}
              />
              <button
                onClick={handleRemoveCover}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}
              >
                ×
              </button>
            </div>
          )}

          <S.FormGrid>
            <S.Label>Etapa</S.Label>
            <S.SelectInput>
              <select
                value={ticket.stepId}
                onChange={(e) => handleStepChange(Number(e.target.value))}
              >
                {steps.map(step => (
                  <option key={step.id} value={step.id}>{step.name}</option>
                ))}
              </select>
            </S.SelectInput>

            <S.Label>Tags</S.Label>
            <S.TagsInputContainer ref={tagContainerRef} onClick={() => setShowTagDropdown(!showTagDropdown)}>
              {(ticket.tags || []).map(tagInTicket => (
                <S.Tag key={tagInTicket.tagId} color={tagInTicket.tag.color} onClick={(e) => e.stopPropagation()}>
                  {tagInTicket.tag.name}
                  <button onClick={() => handleRemoveTag(tagInTicket.tagId)}>×</button>
                </S.Tag>
              ))}
              <S.AddTagButton>
                <S.TagDropdownIcon />
              </S.AddTagButton>
              {showTagDropdown && (
                <S.TagDropdown ref={tagDropdownRef} onClick={(e) => e.stopPropagation()}>
                  {getAvailableTags().length > 0 ? (
                    getAvailableTags().map(tag => (
                      <S.TagDropdownItem key={tag.id} onClick={() => handleAddTag(tag.id)}>
                        <S.TagColor color={tag.color} />
                        {tag.name}
                      </S.TagDropdownItem>
                    ))
                  ) : (
                    <S.TagDropdownEmpty>Nenhuma tag disponível</S.TagDropdownEmpty>
                  )}
                </S.TagDropdown>
              )}
            </S.TagsInputContainer>
            
            <S.Label>Data de vencimento</S.Label>
            <S.Input
              type="date"
              value={ticket.deadline ? new Date(ticket.deadline).toISOString().split('T')[0] : ''}
              onChange={(e) => setTicket({ ...ticket, deadline: new Date(e.target.value) })}
            />

            <S.Label>Urgente</S.Label>
            <S.CheckboxWrapper>
              <S.Checkbox
                checked={ticket.urgency || false}
                onClick={() => setTicket({ ...ticket, urgency: !ticket.urgency })}
              />
            </S.CheckboxWrapper>

            <S.Divider />

            {ticket.fieldInTickets ? (
              ticket.fieldInTickets.map((field) => (
                <RenderField
                  field={field}
                  setValue={setTicket as any}
                  key={field.pipelineFieldId}
                  isFilled={isFilledBoolean}
                  control={control}
                />
              ))
            ) : null}

            <S.Divider />

            <S.DescriptionLabel>Descrição</S.DescriptionLabel>
            <S.TextArea
              value={ticket.description || ''}
              onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
              placeholder="Digite aqui"
            />
          </S.FormGrid>

          <S.SectionDivider />

          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>Anexos</S.SectionTitle>
              <S.SectionAction onClick={() => document.getElementById('file-upload-input')?.click()}>
                <Paperclip size={14} />
                Anexar arquivos
              </S.SectionAction>
              <input
                id="file-upload-input"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </S.SectionHeader>
            <S.AttachmentList>
              {ticket.attachments.map(attachment => {
                console.log('Attachment debug:', {
                  id: attachment.id,
                  originalName: attachment.originalName,
                  mimeType: attachment.mimeType,
                  filePath: attachment.filePath,
                  isImage: attachment.mimeType.startsWith('image/')
                });
                return (
                <S.AttachmentItem key={attachment.id}>
                  <S.AttachmentIcon onClick={() => handleViewAttachment(attachment)}>
                    <S.AttachmentMenu
                      ref={el => attachmentMenuRefs.current[attachment.id] = el}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleAttachmentMenu(attachment.id)
                      }}
                    >
                      ⋯
                    </S.AttachmentMenu>
                    {attachment.mimeType.startsWith('image/') ? (
                      <img
                        src={attachment.filePath}
                        alt={attachment.originalName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          console.error('Erro ao carregar preview da imagem:', attachment.filePath);

                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const icon = document.createElement('div');
                            icon.innerHTML = getFileIcon(attachment.mimeType);
                            icon.style.fontSize = '24px';
                            parent.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <S.FileIcon>{getFileIcon(attachment.mimeType)}</S.FileIcon>
                    )}
                  </S.AttachmentIcon>
                  <S.AttachmentName title={attachment.originalName}>
                    {attachment.originalName}
                  </S.AttachmentName>
                </S.AttachmentItem>
                );
              })}
              {attachmentMenuId !== null && ReactDOM.createPortal(
                <S.AttachmentDropdownPortal
                  ref={attachmentDropdownRef}
                  style={{ top: attachmentMenuPosition.top, left: attachmentMenuPosition.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <S.DropdownItem onClick={() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId)
                    if (att) handleViewAttachment(att)
                  }}>
                    Visualizar
                  </S.DropdownItem>
                  <S.DropdownItem onClick={() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId)
                    if (att) handleDownloadAttachment(att)
                  }}>
                    Download
                  </S.DropdownItem>
                  {(() => {
                    const att = ticket.attachments.find(a => a.id === attachmentMenuId);
                    return att && att.mimeType.startsWith('image/') ? (
                      <S.DropdownItem onClick={() => handleSetCover(att.id)}>
                        Definir como capa
                      </S.DropdownItem>
                    ) : null;
                  })()}
                  <S.DropdownItem className="danger" onClick={() => {
                    if (attachmentMenuId) handleDeleteAttachment(attachmentMenuId)
                  }}>
                    Excluir
                  </S.DropdownItem>
                </S.AttachmentDropdownPortal>,
                document.body
              )}
            </S.AttachmentList>
          </S.Section>

          <S.SectionDivider />

          <S.Section>
            <S.SectionHeader>
              <S.SectionTitle>Tarefas</S.SectionTitle>
              <S.SectionAction onClick={() => setShowAddTask(true)}>
                <Plus size={14} />
                Adicionar tarefa
              </S.SectionAction>
            </S.SectionHeader>
            <S.TaskList>
              {ticket.tasks && ticket.tasks.map(task => (
                <S.TaskItem key={task.id}>
                  <S.TaskCheckbox
                    checked={task.completed}
                    onClick={() => handleToggleTask(task.id, task.completed)}
                  />
                  <S.TaskTitle completed={task.completed}>{task.title}</S.TaskTitle>
                  <S.TaskMenu
                    ref={el => taskMenuRefs.current[task.id] = el}
                    onClick={() => handleToggleTaskMenu(task.id)}
                  >
                    <MoreHorizontal size={16} />
                  </S.TaskMenu>
                </S.TaskItem>
              ))}
              {taskMenuId !== null && ReactDOM.createPortal(
                <S.AttachmentDropdownPortal
                  ref={taskDropdownRef}
                  style={{ top: taskMenuPosition.top, left: taskMenuPosition.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <S.DropdownItem className="danger" onClick={() => {
                    if (taskMenuId) handleDeleteTask(taskMenuId)
                  }}>
                    Excluir
                  </S.DropdownItem>
                </S.AttachmentDropdownPortal>,
                document.body
              )}
              {showAddTask && (
                <S.AddTaskInput>
                  <input
                    type="text"
                    placeholder="Digite o título da tarefa"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
                    autoFocus
                  />
                  <button onClick={handleAddTask}>Adicionar</button>
                  <button onClick={() => { setShowAddTask(false); setNewTaskTitle('') }} style={{ background: '#999' }}>
                    Cancelar
                  </button>
                </S.AddTaskInput>
              )}
            </S.TaskList>
          </S.Section>

          <S.SectionDivider />

          <S.Section>
            <CommentsOfOp ticketId={ticket.id} />
          </S.Section>
        </S.Content>
      </S.Container>
    </FormProvider>
  )
}

export default ViewOrEditTicket
